// Setup › Calls on the SettingsPage pattern (BL-189): what a missed caller gets and when the receptionist
// answers (RULE_SECTIONS), then the company's reasons a call didn't book (callReasons.list, BL-003), in
// the order the call card offers them, each a lead or not and excused or not. As CallReasons.tsx draws
// them in Blueprint, with nothing changed: Add a reason and each reason's Change open a FormSheet where
// the inline form was, and a reason moves up or down the call card from its sheet. A reason calls have
// used is retired, never deleted. While the company mirrors its import source the reasons read, with the
// line (BL-088). Riverstone keeps CallReasonsTab.
import { useCallback, useEffect, useState } from 'react';
import { ActionGroup, Button, Checkbox, Link, TextField } from '@flowology/ui-web/fl';
import { FormSheet, SettingRow, SettingsPage, SettingsSection } from '@flowology/ui-web/patterns';
import { client } from '../api.ts';
import { reasonMeans, type CallReason } from '../calls/reasons-model.ts';
import { refusal } from '../history/customer-edit-model.ts';
import { PageBanners, usePageCrumb, type NavProps } from '../Nav.tsx';
import { MIRROR_LINE, useMirror } from '../shared/mirror.ts';
import { BP_LEDE } from './ledes.ts';
import { settingsPage } from './parts.ts';
import { settingsRights } from './settings-model.ts';
import { SetupRuleSections } from './SetupRules.tsx';

type Draft = {
  id?: string;
  name: string;
  isLead: boolean;
  excused: boolean;
  active: boolean;
  rowVersion?: number;
};
const REASONS_LINE =
  'Why a call didn’t book. A lead counts toward the booking rate; an excused reason doesn’t count against whoever took the call. Calls under a minute are excused whatever their reason.';
const draftOf = (r: CallReason): Draft => ({
  id: r.id,
  name: r.name,
  isLead: r.isLead,
  excused: r.excused,
  active: r.active,
  rowVersion: r.rowVersion,
});

/** Saves a reason (callReasons.save), at `sort` when it moves; the server's refusal in its words. */
async function saveReason(d: Draft, sort?: number): Promise<CallReason> {
  try {
    return await client.call<CallReason>('callReasons.save', {
      ...(d.id ? { id: d.id, rowVersion: d.rowVersion } : {}),
      name: d.name.trim(),
      isLead: d.isLead,
      excused: d.excused,
      active: d.active,
      ...(sort !== undefined ? { sort } : {}),
    });
  } catch (e) {
    throw new Error(refusal(e), { cause: e });
  }
}

export function CallsPage({ nav }: { nav: NavProps }) {
  const rights = settingsRights(nav.permissions ?? []);
  // the import source's while the company mirrors it (BL-088): read, never changed
  const mirroring = useMirror();
  const crumb = usePageCrumb(nav);
  return (
    <div className="app">
      {/* the sheet pads the page (blueprint.css .bp-sheet), as PageFrame does; no .page padding on top */}
      <main>
        <PageBanners nav={nav} />
        <SettingsPage
          head={{
            ...(crumb ? { crumb } : {}),
            title: settingsPage('calls').label ?? 'Calls',
            lede: BP_LEDE.calls,
          }}
        >
          <SetupRuleSections page="calls" top rights={rights} />
          <Reasons canEdit={rights.edit && !mirroring} mirroring={mirroring && rights.edit} />
          <SetupRuleSections page="calls" rights={rights} />
        </SettingsPage>
      </main>
    </div>
  );
}

function Reasons({ canEdit, mirroring }: { canEdit: boolean; mirroring: boolean }) {
  const [reasons, setReasons] = useState<CallReason[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<Draft | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  const load = useCallback(() => {
    client
      .call<{ reasons: CallReason[] }>('callReasons.list', { includeRetired: true })
      .then((r) => {
        setReasons(r.reasons);
        setError(null);
      })
      .catch((e: unknown) => setError(refusal(e)));
  }, []);
  useEffect(load, [load]);
  const saved = (r: CallReason) => {
    setSaid(`${r.name} saved.`);
    load();
  };
  return (
    <>
      <SettingsSection
        title="Call reasons"
        line={REASONS_LINE}
        {...(canEdit
          ? {
              action: (
                <Button
                  variant="primary"
                  onPress={() => setOpen({ name: '', isLead: true, excused: false, active: true })}
                >
                  Add a reason
                </Button>
              ),
            }
          : {})}
        {...(mirroring || said
          ? {
              note: (
                <>
                  {mirroring ? MIRROR_LINE : null}
                  {mirroring && said ? ' ' : null}
                  {said ? <span role="status">{said}</span> : null}
                </>
              ),
            }
          : {})}
        status={reasons ? (reasons.length ? 'ready' : 'empty') : error ? 'failed' : 'loading'}
        emptyText="No call reasons yet."
        {...(error ? { failedText: error } : {})}
        onRetry={load}
      >
        {reasons?.map((r) => (
          <SettingRow
            key={r.id}
            name={r.name}
            {...(r.active ? {} : { tag: 'Retired' })}
            help={reasonMeans(r)}
            control={
              canEdit ? (
                <Link label={`Change ${r.name}`} onPress={() => setOpen(draftOf(r))}>
                  Change
                </Link>
              ) : null
            }
          />
        ))}
      </SettingsSection>
      {/* over the page: a section's body is its empty or failed line until it has rows */}
      {open && (
        <ReasonSheet
          key={open.id ?? 'new'}
          first={open}
          reasons={reasons ?? []}
          onSaved={saved}
          onClose={() => setOpen(null)}
        />
      )}
    </>
  );
}

/** A new reason, or one to change: its name, a lead or not, excused or not, retired; and where it sits. */
function ReasonSheet({
  first,
  reasons,
  onSaved,
  onClose,
}: {
  first: Draft;
  reasons: readonly CallReason[];
  onSaved: (r: CallReason) => void;
  onClose: () => void;
}) {
  // what's saved: a move saves the reason and keeps the sheet open on it
  const [kept, setKept] = useState(first);
  const [d, setD] = useState(first);
  const [moving, setMoving] = useState(false);
  const [moveError, setMoveError] = useState<string | null>(null);
  const at = d.id ? reasons.findIndex((r) => r.id === d.id) : -1;
  // it takes its neighbour's place (the neighbour moves on its next save)
  const move = (dir: -1 | 1) => {
    const b = reasons[at + dir];
    if (!b || moving) return;
    setMoving(true);
    setMoveError(null);
    saveReason(d, b.sort + dir)
      .then((r) => {
        const now = draftOf(r);
        setKept(now);
        setD(now);
        onSaved(r);
      })
      .catch((e: unknown) => setMoveError(e instanceof Error ? e.message : String(e)))
      .finally(() => setMoving(false));
  };
  return (
    <FormSheet
      isOpen
      title={d.id ? `Change ${kept.name}` : 'A new call reason'}
      submitLabel="Save"
      isDirty={JSON.stringify(d) !== JSON.stringify(kept)}
      isSubmitDisabled={!d.name.trim() || moving}
      {...(moveError ? { note: moveError } : {})}
      onClose={onClose}
      onSubmit={async () => onSaved(await saveReason(d))}
    >
      <TextField label="Name" maxLength={120} value={d.name} onChange={(name) => setD({ ...d, name })} />
      <Checkbox isSelected={d.isLead} onChange={(isLead) => setD({ ...d, isLead })}>
        A lead: it counts toward the booking rate
      </Checkbox>
      <Checkbox isSelected={d.excused} onChange={(excused) => setD({ ...d, excused })}>
        Excused: it doesn’t count against whoever took the call
      </Checkbox>
      {d.id && (
        <Checkbox isSelected={!d.active} onChange={(retired) => setD({ ...d, active: !retired })}>
          Retired: no longer offered on a call
        </Checkbox>
      )}
      {d.id && at >= 0 && (
        <ActionGroup
          label="Where it sits on the call card"
          items={[
            { id: 'up', label: 'Move it up', isDisabled: moving || at === 0 },
            { id: 'down', label: 'Move it down', isDisabled: moving || at === reasons.length - 1 },
          ]}
          onAction={(id) => move(id === 'up' ? -1 : 1)}
        />
      )}
    </FormSheet>
  );
}
